import { formatFieldName } from "./acrValidationUtils";
/**
 * Group conditional errors by their parent field
 * Returns a map where key is parent field name, value is array of child errors
 */
export function groupErrorsByParent(errors) {
  var parentMap = new Map();
  errors.forEach(function (error) {
    var _error$conditional_me;
    if (error.type === "conditional" && (_error$conditional_me = error.conditional_metadata) !== null && _error$conditional_me !== void 0 && _error$conditional_me.field) {
      var parentField = error.conditional_metadata.field;
      if (!parentMap.has(parentField)) {
        parentMap.set(parentField, []);
      }
      parentMap.get(parentField).push(error);
    }
  });
  return parentMap;
}

/**
 * Create hierarchical structure of parent fields with their conditional children
 * Returns array of grouped errors with parent and children
 */
export function createFieldHierarchy(errors) {
  var parentMap = groupErrorsByParent(errors);
  var result = [];
  var processedFields = new Set();
  errors.forEach(function (error) {
    // Skip if already processed as a child
    if (processedFields.has(error.field)) return;

    // Check if this field has children (is a parent)
    var children = parentMap.get(error.field) || [];
    if (children.length > 0) {
      // This is a parent field with conditional children
      result.push({
        field: error.field,
        error: error,
        children: children
      });

      // Mark children as processed
      children.forEach(function (child) {
        return processedFields.add(child.field);
      });
    } else if (error.type !== "conditional") {
      // Non-conditional error without children
      result.push({
        field: error.field,
        error: error,
        children: []
      });
    } else {
      // Conditional error but parent not in error list
      // Still show it but without parent context
      result.push({
        field: error.field,
        error: error,
        children: []
      });
    }
    processedFields.add(error.field);
  });
  return result;
}

/**
 * Get error type styling class
 */
export function getErrorTypeClass(error) {
  if (error.type === "required") return "error-critical";
  if (error.type === "validation") return "error-format";
  if (error.type === "conditional") {
    var _error$conditional_me2;
    return ((_error$conditional_me2 = error.conditional_metadata) === null || _error$conditional_me2 === void 0 ? void 0 : _error$conditional_me2.type) === "required" ? "error-conditional" : "error-info";
  }
  return "error-default";
}

/**
 * Get error icon color based on error type
 */
export function getErrorIconColor(error) {
  if (error.type === "required" || error.type === "validation") {
    return "text-red-500";
  }
  if (error.type === "conditional") {
    var _error$conditional_me3;
    return ((_error$conditional_me3 = error.conditional_metadata) === null || _error$conditional_me3 === void 0 ? void 0 : _error$conditional_me3.type) === "required" ? "text-orange-500" : "text-blue-500";
  }
  return "text-red-500";
}

/**
 * Format conditional context message
 */
export function formatConditionalContext(metadata) {
  if (!metadata) return null;
  var fieldLabel = formatFieldName(metadata.field);
  var value = metadata.value || "populated";
  return "Since ".concat(fieldLabel, " is ").concat(value);
}

/**
 * Check if a field is a parent field (has conditional children)
 */
export function isParentField(fieldName, errors) {
  return errors.some(function (error) {
    var _error$conditional_me4;
    return error.type === "conditional" && ((_error$conditional_me4 = error.conditional_metadata) === null || _error$conditional_me4 === void 0 ? void 0 : _error$conditional_me4.field) === fieldName;
  });
}

/**
 * Get children errors for a parent field
 */
export function getChildrenErrors(parentField, errors) {
  return errors.filter(function (error) {
    var _error$conditional_me5;
    return error.type === "conditional" && ((_error$conditional_me5 = error.conditional_metadata) === null || _error$conditional_me5 === void 0 ? void 0 : _error$conditional_me5.field) === parentField;
  });
}